<template>
  <page-outline 
    :title="$t('Webheader.entity_account')"
    :show-filter="tabName === 1 || tabName === 2"
    :show-refresh="tabName === 1 || tabName === 2"
    :filter-expanded="displayFilter"
    :refresh-disabled="false"
    @toggle-filter="openFilter"
    @refresh="refresh">
    <template slot="navigationTabs">
      <!-- Version Mobile - 1 colonne avec plusieurs lignes -->
      <div class="d-block d-lg-none">
        <div class="d-flex flex-column">
          <app-button
            v-for="(tab, index) in tabs"
            :key="index"
            :text="$t(tab.translationKey)"
            :variant="tabName === tab.value ? 'primary' : 'light'"
            size="md"
            :block="true"
            custom-class="mb-2"
            :aria-pressed="tabName === tab.value ? 'true' : 'false'"
            @click="changeTabName(tab.value)"
          />
        </div>
      </div>
      
      <!-- Version Desktop - Onglets Bootstrap qui prennent toute la largeur -->
      <div class="d-none d-lg-block">
        <b-nav tabs fill class="border-0" role="tablist">
          <b-nav-item
            v-for="(tab, index) in tabs"
            :key="index"
            :active="tabName === tab.value"
            @click="changeTabName(tab.value)"
            :class="{'bg-dark text-white': tabName === tab.value}"
            :id="'tab-entityaccount-' + tab.value"
            role="tab"
            :aria-selected="tabName === tab.value ? 'true' : 'false'"
            :aria-controls="'tabpanel-entityaccount-' + tab.value"
          >
            <span :class="{'font-weight-bold': tabName === tab.value}">
              {{ $t(tab.translationKey) }}
            </span>
          </b-nav-item>
        </b-nav>
      </div>
    </template>
    
    <template slot="filterContent" v-if="tabName === 1 || tabName === 2">
      <app-panel
        :filters="filterConfig"
        :searchAriaLabel="$t('common.srch') + ' ' + $t('Webheader.entity_account_file')"
        @filter-changed="onFilterChanged"
        @clear-filters="clearFilterFields"
        @apply-filters="applyFilters"
      />
    </template>
    
    <template slot="pageContent">
      <b-container fluid>
        <div class="requestsList">
          <div role="tabpanel" id="tabpanel-entityaccount-1" :aria-labelledby="'tab-entityaccount-1'" v-if="tabName === 1">
            <EntityAccountFileList></EntityAccountFileList>
          </div>
          <div role="tabpanel" id="tabpanel-entityaccount-2" :aria-labelledby="'tab-entityaccount-2'" v-if="tabName === 2">
            <EntityAccountList></EntityAccountList>
          </div>
        </div>
      </b-container>
    </template>
  </page-outline>
</template>

<script>
import PageOutline from '@/components/common/PageOutline.vue'
import EntityAccountList from '@/pages/entityAccount/EntityAccountList.vue'
import EntityAccountFileList from '@/pages/entityAccount/EntityAccountFileList.vue'
import AppButton from '@/components/common/AppButton.vue'
import AppPanel from '@/components/common/AppPanel.vue'

export default {
  name: 'EntityAccountPage',
  components: {
    EntityAccountList,
    EntityAccountFileList,
    'page-outline': PageOutline,
    AppButton,
    AppPanel
  },
  props: {
    additionalClass: {
      type: String,
      default: ''
    },
    type: {
      default: 'EntityAccountFile',
      type: String
    },
    id: {
      default: 'NULL',
      type: String
    }
  },
  data () {
    return {
      tabName: 1,
      displayFilter: false,
      tabs: [
        { translationKey: 'Webheader.entity_account_file', value: 1 },
        { translationKey: 'Webheader.iban_history', value: 2 }
      ],
      // Filtres pour Fichier du compte entité (onglet 1)
      filterValues: {
        startDate: '',
        endDate: '',
        filename: '',
        fileReference: '',
        fileStatus: '',
        fileType: '',
        iban: '',
        ibanReference: ''
      }
    }
  },
  computed: {
    filterConfig() {
      if (this.tabName === 1) {
        return [
          {
            id: 'filter-start-date',
            type: 'datetime',
            prop: 'startDate',
            labelKey: 'filter.start_date',
            icon: 'fa-solid fa-calendar-days',
            value: this.filterValues.startDate,
            format: 'DD/MM/YYYY H:i:s',
            maxLength: 19,
            colSm: '6',
            colLg: '3'
          },
          {
            id: 'filter-end-date',
            type: 'datetime',
            prop: 'endDate',
            labelKey: 'filter.end_date',
            icon: 'fa-solid fa-calendar-days',
            value: this.filterValues.endDate,
            format: 'DD/MM/YYYY H:i:s',
            maxLength: 19,
            colSm: '6',
            colLg: '3'
          },
          {
            id: 'filter-filename',
            type: 'text',
            prop: 'filename',
            labelKey: 'filter.filename',
            icon: 'fa-solid fa-file',
            value: this.filterValues.filename,
            colSm: '6',
            colLg: '3'
          },
          {
            id: 'filter-file-reference',
            type: 'text',
            prop: 'fileReference',
            labelKey: 'filter.file_reference',
            icon: 'fa-solid fa-hashtag',
            value: this.filterValues.fileReference,
            colSm: '6',
            colLg: '3'
          },
          {
            id: 'filter-file-status',
            type: 'select',
            prop: 'fileStatus',
            labelKey: 'filter.file_status',
            icon: 'fa-solid fa-flag',
            value: this.filterValues.fileStatus,
            defaultOptionKey: 'filter.select_file_status',
            colSm: '6',
            colLg: '6',
            options: [
              { value: '1', labelKey: 'common.entity_account.file_status_1' },
              { value: '2', labelKey: 'common.entity_account.file_status_2' },
              { value: '3', labelKey: 'common.entity_account.file_status_3' },
              { value: '4', labelKey: 'common.entity_account.file_status_4' },
              { value: '5', labelKey: 'common.entity_account.file_status_5' },
              { value: '6', labelKey: 'common.entity_account.file_status_6' },
              { value: '7', labelKey: 'common.entity_account.file_status_7' },
              { value: '8', labelKey: 'common.entity_account.file_status_8' },
              { value: '9', labelKey: 'common.entity_account.file_status_9' },
              { value: '20', labelKey: 'common.entity_account.file_status_20' },
              { value: '21', labelKey: 'common.entity_account.file_status_21' },
              { value: '22', labelKey: 'common.entity_account.file_status_22' },
              { value: '91', labelKey: 'common.entity_account.file_status_91' },
              { value: '92', labelKey: 'common.entity_account.file_status_92' },
              { value: '93', labelKey: 'common.entity_account.file_status_93' }
            ]
          },
          {
            id: 'filter-file-type',
            type: 'select',
            prop: 'fileType',
            labelKey: 'common.entity_account.synchro_type',
            icon: 'fa-solid fa-file-lines',
            value: this.filterValues.fileType,
            defaultOptionKey: 'common.entity_account.select_synchro_type',
            colSm: '6',
            colLg: '6',
            options: [
              { value: 'REPLACEFULL', labelKey: 'common.entity_account.synchro_type_REPLACEFULL' },
              { value: 'DELTA', labelKey: 'common.entity_account.synchro_type_DELTA' },
              { value: 'DELETEDELTA', labelKey: 'common.entity_account.synchro_type_DELETEDELTA' },
              { value: 'UNRECOGNIZED', labelKey: 'common.entity_account.synchro_type_UNRECOGNIZED' }
            ]
          }
        ]
      }
      else{
        return [
          {
            id: 'filter-iban-encrypted',
            type: 'text',
            prop: 'iban',
            labelKey: 'common.entity_account.iban_encrypted',
            icon: 'fa-solid fa-file',
            value: this.filterValues.iban,
            colSm: '6',
            colLg: '3'
          },
          {
            id: 'filter-iban-reference',
            type: 'text',
            prop: 'ibanReference',
            labelKey: 'common.entity_account.iban_reference',
            icon: 'fa-solid fa-file',
            value: this.filterValues.ibanReference,
            colSm: '6',
            colLg: '3'
          }
        ]
      }
    }
  },
  created () {
    if (this.type === 'EntityAccountFile') {
      this.tabName = 1
    } else if (this.type === 'EntityAccount') {
      this.tabName = 2
    }
  },
  mounted () {
    this.$$('.infinite-scroll-preloader').remove()
    
    // Synchronisation de l'état des filtres avec les composants enfants
    this.$bus.$off('sync-filter-state')
    this.$bus.$on('sync-filter-state', (state) => {
      this.displayFilter = state
    })
  },
  methods: {
    changeTabName (tabName) {
      // Ferme les filtres lors du changement d'onglet
      this.displayFilter = false
      
      // Synchronise avec les composants enfants pour l'onglet 1 seulement
      if (this.tabName === 1) {
        this.$bus.$emit('close-filter-entity-file')
      }
      
      this.tabName = tabName
    },
    openFilter () {
      // Toggle des filtres avec icône dynamique
      this.displayFilter = !this.displayFilter
      
      // Informe les composants enfants de l'état du filtre
      if (this.tabName === 1) {
        this.$bus.$emit('toggle-filter-entity-file')
      }
    },
    refresh () {
      // Déclenche l'actualisation dans le composant enfant approprié
      if (this.tabName === 1) {
        // Pour EntityAccountFileList
        this.$bus.$emit('refresh-entity-file')
      }
    },
    onFilterChanged({ prop, value }) {
      this.filterValues[prop] = value
    },
    clearFilterFields () {
      // Nettoyer les filtres de l'onglet Fichier du compte entité
      this.filterValues = {
        startDate: '',
        endDate: '',
        filename: '',
        fileReference: '',
        fileStatus: '',
        iban: '',
        ibanReference: '',
        fileType: ''
      }
      this.applyFilters()
    },
    applyFilters () {
      // Filtres pour Fichier du compte entité
      // Les dates sont déjà au format DD/MM/YYYY HH:mm:ss depuis le datetime picker
      if (this.tabName === 1 ){

        const filters = {
          startDate: this.filterValues.startDate,
          endDate: this.filterValues.endDate,
          filename: this.filterValues.filename,
          fileReference: this.filterValues.fileReference,
          fileStatus: this.filterValues.fileStatus,
          fileType: this.filterValues.fileType
        }
        this.$bus.$emit('apply-filters-entity-file', filters)
      }
      else {
        const filters = {
          iban: this.filterValues.iban,
          ibanReference: this.filterValues.ibanReference
        }
        this.$bus.$emit('apply-filters-entity', filters)
      }
    }
  }
}
</script>

<style scoped>
/* Styles pour les onglets de navigation */
::v-deep .nav-tabs .nav-item {
  text-align: center;
}

/* Version desktop - onglets qui prennent toute la largeur */
@media (min-width: 992px) {
  ::v-deep .nav-tabs.nav-fill .nav-item {
    flex: 1;
  }
}

::v-deep .nav-tabs .nav-link {
  border: 1px solid #dee2e6 !important;
  background-color: #f8f9fa !important;
  color: #495057 !important;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

::v-deep .nav-tabs .nav-link:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
  color: #212529 !important;
}

::v-deep .nav-tabs .nav-link.active {
  background-color: #0d7377 !important;
  color: white !important;
  border-color: #0d7377 !important;
  font-weight: bold;
}

::v-deep .nav-tabs .nav-link.active:hover {
  background-color: #0a5d61 !important;
  color: white !important;
  border-color: #0a5d61 !important;
}

/* Ensure all child elements of active tab have proper contrast */
::v-deep .nav-tabs .nav-link.active span {
  color: white !important;
}

::v-deep .nav-tabs .nav-link.active .font-weight-bold {
  color: white !important;
  background-color: transparent !important;
}

/* Force la couleur noire pour les labels de filtres pour un meilleur contraste RGAA */
.form-label.text-dark {
  color: #212529 !important;
}

/* Gutter des rows pour Bootstrap 4 */
.g-3 {
  margin-right: -0.5rem;
  margin-left: -0.5rem;
}

.g-3 > [class*="col-"] {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.gx-2 {
  margin-right: -0.25rem;
  margin-left: -0.25rem;
}

.gx-2 > [class*="col-"] {
  padding-right: 0.25rem;
  padding-left: 0.25rem;
}
</style>
